import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, ShieldCheck, Ban, Flag, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Avatar, VerifiedBadge } from "@/components/Brand";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDmSocket } from "@/components/Shortcuts";

const time = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

function ConvList({ convs, active }) {
  if (!convs.length) return <p className="p-6 text-center text-sm text-muted-foreground" data-testid="dm-empty">Nenhuma conversa ainda. Abra um perfil e toque em "Mensagem".</p>;
  return convs.map((c) => (
    <Link key={c.id} to={`/mensagens/${c.id}`} data-testid={`dm-conv-${c.id}`}
      className={`flex items-center gap-3 rounded-2xl p-3 transition-colors ${active === c.id ? "bg-white/10" : "hover:bg-white/5"}`}>
      <Avatar name={c.other.name} src={c.other.avatar} size={44} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-sm font-bold">{c.other.name}{c.other.verified && <VerifiedBadge size={13} />}</p>
        <p className={`truncate text-xs ${c.unread ? "font-bold text-white" : "text-muted-foreground"}`}>{c.last_message?.text}</p>
      </div>
      {c.unread > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-rose-500 px-1.5 text-[10px] font-extrabold" data-testid={`dm-unread-${c.id}`}>{c.unread}</span>}
    </Link>
  ));
}

function Chat({ convId, me, incoming, onSent }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const end = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { setData(null); api.get(`/dm/conversations/${convId}/messages`).then((r) => setData(r.data)).catch((e) => { toast.error(formatApiError(e)); navigate("/mensagens"); }); }, [convId, navigate]);
  useEffect(() => {
    if (incoming?.conv_id !== convId) return;
    setData((d) => d && !d.messages.some((m) => m.id === incoming.message.id) ? { ...d, messages: [...d.messages, incoming.message] } : d);
    if (incoming.message.sender_id !== me) api.get(`/dm/conversations/${convId}/messages`).catch(() => {});
  }, [incoming, convId, me]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [data?.messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data: m } = await api.post(`/dm/conversations/${convId}/messages`, { text });
      setData((d) => d.messages.some((x) => x.id === m.id) ? d : { ...d, messages: [...d.messages, m] });
      setText("");
      onSent();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSending(false); }
  };
  const block = async () => { const { data: r } = await api.post(`/dm/conversations/${convId}/block`); setData((d) => ({ ...d, conversation: { ...d.conversation, blocked_by_me: r.blocked_by_me, blocked: r.blocked } })); toast.success(r.blocked_by_me ? "Conversa bloqueada" : "Conversa desbloqueada"); };
  const report = async () => { await api.post(`/dm/conversations/${convId}/report`); toast.success("Denúncia enviada à moderação. A conversa foi bloqueada."); setData((d) => ({ ...d, conversation: { ...d.conversation, blocked_by_me: true, blocked: true } })); };

  if (!data) return <div className="grid flex-1 place-items-center text-sm text-muted-foreground">Carregando…</div>;
  const o = data.conversation.other;
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-testid="dm-chat">
      <div className="flex items-center gap-3 border-b border-white/5 p-3">
        <Link to="/mensagens" className="grid h-9 w-9 place-items-center rounded-full hover:bg-white/10 md:hidden" data-testid="dm-back-button"><ArrowLeft size={18} /></Link>
        <Link to={`/perfil/${o.id}`} className="flex min-w-0 flex-1 items-center gap-3"><Avatar name={o.name} src={o.avatar} size={38} />
          <p className="flex items-center gap-1.5 truncate font-bold">{o.name}{o.verified && <VerifiedBadge size={14} />}</p></Link>
        <DropdownMenu>
          <DropdownMenuTrigger className="rounded-full px-3 py-1.5 text-xs font-bold text-muted-foreground hover:bg-white/10" data-testid="dm-options-button">Opções</DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={block} data-testid="dm-block-button"><Ban size={14} className="mr-2" />{data.conversation.blocked_by_me ? "Desbloquear" : "Bloquear"}</DropdownMenuItem>
            <DropdownMenuItem onClick={report} className="text-red-300" data-testid="dm-report-button"><Flag size={14} className="mr-2" />Denunciar conversa</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="dm-messages">
        <p className="mx-auto mb-4 flex w-fit items-center gap-1.5 rounded-full bg-violet-500/10 px-3 py-1 text-[11px] text-violet-300"><ShieldCheck size={12} /> Conversa protegida pelo Guarda-Costas IA</p>
        {data.messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender_id === me ? "justify-end" : "justify-start"}`} data-testid={`dm-msg-${m.id}`}>
            <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${m.sender_id === me ? "rounded-br-md bg-[hsl(var(--gold))] text-[#0B0F17]" : "rounded-bl-md bg-white/10"}`}>
              <p className="whitespace-pre-wrap break-words">{m.text}</p>
              <p className={`mt-0.5 text-right text-[10px] ${m.sender_id === me ? "text-black/50" : "text-muted-foreground"}`}>{time(m.created_at)}</p>
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      {data.conversation.blocked
        ? <p className="border-t border-white/5 p-4 text-center text-sm text-muted-foreground" data-testid="dm-blocked-note">Esta conversa está bloqueada.</p>
        : <form onSubmit={send} className="flex gap-2 border-t border-white/5 p-3">
            <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Escreva uma mensagem…" className="h-11 rounded-full bg-white/5" data-testid="dm-input" />
            <button type="submit" disabled={sending || !text.trim()} className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--gold))] text-[#0B0F17] transition-transform active:scale-90 disabled:opacity-50" data-testid="dm-send-button"><Send size={17} /></button>
          </form>}
    </div>
  );
}

export default function Messages() {
  const { convId } = useParams();
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [incoming, setIncoming] = useState(null);
  const load = useCallback(() => api.get("/dm/conversations").then((r) => setConvs(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, convId]);
  const onEvent = useCallback((e) => { if (e.type === "dm") { setIncoming(e); load(); } }, [load]);
  useDmSocket(onEvent);

  return (
    <div className="mx-auto flex h-[calc(100dvh-9rem)] max-w-5xl overflow-hidden rounded-3xl border border-white/5 bg-card/60 md:h-[calc(100dvh-6rem)] md:mt-4" data-testid="messages-page">
      <aside className={`w-full shrink-0 overflow-y-auto border-r border-white/5 p-2 md:block md:w-80 ${convId ? "hidden" : "block"}`}>
        <h1 className="flex items-center gap-2 px-2 py-3 text-xl font-extrabold"><MessageCircle size={20} className="text-[hsl(var(--gold))]" /> Mensagens</h1>
        <ConvList convs={convs} active={convId} />
      </aside>
      {convId ? <Chat convId={convId} me={user.id} incoming={incoming} onSent={load} />
        : <div className="hidden flex-1 place-items-center text-sm text-muted-foreground md:grid">Escolha uma conversa</div>}
    </div>
  );
}
